/* lib/dialogHold.js — реестр открытых диалогов: пока он не пуст, WorkspaceTabSync не переключает
   вкладку в пространство, выбранное в соседней (kitchen-ux A2, круги 4–5, S10).

   Зачем. Поток «прочитать → спросить → записать» ждёт ответа в диалоге, сеть в эту паузу тихая —
   переход вкладки случился бы именно тогда, и окно пропало бы посреди работы. Реестр откладывает
   переход, пока учтённые диалоги открыты (кнопка «Переключить сейчас» тоже ждёт их закрытия).

   Что учтено — только то, что зарегистрировалось само (поиска по DOM нет):
   - Modal UI-kit (components/ui/Modal.jsx) — пока open;
   - ConfirmProvider — с вызова confirm() (синхронно, до отрисовки) и до ответа;
   - компоненты, которые зовут useDialogHold сами, — например, мастер бизнес-пространства,
     карточки туров онбординга (подсказки поверх приложения — нет), окно отправки в POS.
   Остальное НЕ держит: Drawer'ы (открытый редактор — обычное состояние вкладки, несохранённое
   защищает сторож) и свои оверлеи мимо Modal, пока не позовут useDialogHold(open) сами.

   Чего реестр НЕ делает: не закрепляет пространство записи. Запись, которой оно важно, берёт
   pinWorkspace() в начале потока и шлёт его явным заголовком на каждом шаге (lib/api/client.js);
   вход другого пользователя ловит замок сессии, а не реестр. */

import React from 'react';

const HOLD = { count: 0, listeners: new Set() };

function changed() {
  HOLD.listeners.forEach((fn) => { try { fn(); } catch { /* noop */ } });
}

/* Учесть открытый диалог → функция «снять» (повторный вызов — ничего). */
export function holdDialog() {
  HOLD.count += 1;
  changed();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    HOLD.count = Math.max(0, HOLD.count - 1);
    changed();
  };
}

/* Сколько диалогов открыто сейчас. */
export function dialogsOpen() {
  return HOLD.count;
}

/* → Promise<boolean>: true — открытых диалогов нет (сразу, если нет и сейчас); false — ожидание
   отменено (signal), слушатель снят. */
export function whenNoDialogs({ signal } = {}) {
  if (signal && signal.aborted) return Promise.resolve(false);
  if (HOLD.count === 0) return Promise.resolve(true);
  return new Promise((resolve) => {
    let onAbort = null;
    const check = () => { if (HOLD.count === 0) finish(true); };
    function finish(v) {
      HOLD.listeners.delete(check);
      if (signal && onAbort) signal.removeEventListener('abort', onAbort);
      resolve(v);
    }
    onAbort = () => finish(false);
    HOLD.listeners.add(check);
    if (signal) signal.addEventListener('abort', onAbort);
  });
}

// коммит-эффект в браузере (диалог учтён до того, как его увидят); на сервере — обычный
const useCommitEffect = typeof window !== 'undefined' ? React.useLayoutEffect : React.useEffect;

/* Хук диалога: пока active — вкладка держится в своём пространстве. */
export function useDialogHold(active) {
  const on = !!active;
  useCommitEffect(() => (on ? holdDialog() : undefined), [on]);
}
